import {
  ClientParams,
  ClientsResponse,
  createClient,
  CreateClientDto,
  deleteClient,
  deleteMultipleClients,
  findClients,
  getClient,
  updateClient,
  UpdateClientDto,
} from '@/api/client.api';
import { queryKeys } from '@/queryKeys';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

export const useFindClients = <TData = ClientsResponse>(
  params: ClientParams,
  select?: (data: ClientsResponse) => TData,
) =>
  useQuery({
    queryKey: queryKeys.clients.list(params),
    queryFn: () => findClients(params),
    select,
    placeholderData: keepPreviousData,
  });

export const useCreateClient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clientData: CreateClientDto) => createClient(clientData),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all }),
  });
};

export const useGetClient = (clientId: string) =>
  useQuery({
    queryKey: queryKeys.clients.detail(clientId),
    queryFn: () => getClient(clientId),
  });

export const useUpdateClient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      clientData,
      clientId,
    }: {
      clientId: string;
      clientData: UpdateClientDto;
    }) => updateClient(clientId, clientData),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all }),
  });
};

export const useDeleteClient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clientId: string) => deleteClient(clientId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all }),
  });
};

export const useDeleteMultipleClients = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clientIds: string[]) => deleteMultipleClients(clientIds),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all }),
  });
};
